
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, CheckCircle2, HelpCircle, MessageSquareWarning, Bug, Zap, Wifi, ShieldAlert } from 'lucide-react';

const TroubleshootingGuide: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Troubleshooting Guide</h2>
        <Badge variant="outline" className="text-xs">Last updated: April 2025</Badge>
      </div>
      
      <Alert>
        <HelpCircle className="h-4 w-4" />
        <AlertDescription>
          Most common issues can be resolved by verifying your installation key and checking browser console for errors.
        </AlertDescription>
      </Alert>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-base">
              <Bug className="h-4 w-4 mr-2 text-red-500" />
              Common Issues
            </CardTitle>
            <CardDescription>Most frequently encountered problems</CardDescription>
          </CardHeader>
          <CardContent className="pt-0 text-sm">
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
              <li>Widget doesn't appear</li>
              <li>Voice commands not working</li>
              <li>Settings not saving</li>
              <li>Style conflicts</li>
              <li>Accessibility panel not opening</li>
            </ul>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-base">
              <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
              Quick Fixes
            </CardTitle>
            <CardDescription>Simple solutions to try first</CardDescription>
          </CardHeader>
          <CardContent className="pt-0 text-sm">
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
              <li>Verify your installation key</li>
              <li>Check browser console for errors</li>
              <li>Test in incognito/private mode</li>
              <li>Clear browser cache</li>
              <li>Ensure HTTPS is being used</li>
            </ul>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-base">
              <MessageSquareWarning className="h-4 w-4 mr-2 text-amber-500" />
              Support Channels
            </CardTitle>
            <CardDescription>Get help with complex issues</CardDescription>
          </CardHeader>
          <CardContent className="pt-0 text-sm">
            <ul className="list-disc pl-4 space-y-1 text-muted-foreground">
              <li>Email: support@accesstopia.com</li>
              <li>Documentation: docs.accesstopia.com</li>
              <li>Forum: community.accesstopia.com</li>
              <li>GitHub: github.com/accesstopia/issues</li>
              <li>Live chat: Available for premium users</li>
            </ul>
          </CardContent>
        </Card>
      </div>
      
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="installation">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center">
              <Zap className="h-4 w-4 mr-2 text-amber-500" />
              Installation Issues
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-sm mb-1">Widget Not Appearing</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If the widget button is not appearing on your website:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Verify that the script is properly added before the closing <code>&lt;/body&gt;</code> tag</li>
                  <li>Check that your installation key is correct and active in your dashboard</li>
                  <li>Ensure the domain matches the one registered with your subscription</li>
                  <li>Look for JavaScript errors in the browser console</li>
                  <li>Test if Content Security Policy (CSP) is blocking script execution</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Console Error: Invalid Key</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If you see an "Invalid installation key" error:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Double-check the key format and ensure there are no typos</li>
                  <li>Verify that your subscription is active in the dashboard</li>
                  <li>Check if you've reached your usage limit for the current plan</li>
                  <li>Try generating a new installation key from your dashboard</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Console Error: Domain Mismatch</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If you see a "Domain not authorized" error:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Verify the domain in your dashboard matches your website's domain</li>
                  <li>Include both www and non-www versions if necessary</li>
                  <li>For development, add localhost to your authorized domains</li>
                  <li>Wait up to 5 minutes for domain changes to propagate</li>
                </ol>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="voice">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center">
              <Zap className="h-4 w-4 mr-2 text-amber-500" />
              Voice Assistant Problems
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-sm mb-1">Voice Assistant Not Working</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If the voice assistant isn't functioning:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Ensure you're using a supported browser (Chrome, Edge, Safari)</li>
                  <li>Check that microphone permissions are granted to your website</li>
                  <li>Verify <code>enableVoiceAssistant: true</code> in your configuration</li>
                  <li>Ensure your site is served over HTTPS (required for microphone access)</li>
                  <li>Try specifying the language explicitly: <code>language: 'en-US'</code></li>
                </ol>
                
                <Alert className="mt-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Voice recognition is not supported in Firefox and some mobile browsers. The widget will automatically hide voice features when unsupported.
                  </AlertDescription>
                </Alert>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Voice Commands Not Recognized</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If the assistant isn't recognizing commands properly:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Ensure you're speaking clearly and at a normal pace</li>
                  <li>Check that the correct language is set in the configuration</li>
                  <li>Try using the exact command phrases from the documentation</li>
                  <li>Test in a quiet environment with minimal background noise</li>
                  <li>Check if your microphone is working properly in other applications</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Custom Voice Commands Not Working</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If your custom commands aren't being recognized:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Verify the custom commands JSON syntax is correct</li>
                  <li>Check that the commands are registered before the widget loads</li>
                  <li>Try adding alternative phrasings for better recognition</li>
                  <li>Use the debug mode to see what phrases are being detected</li>
                </ol>
                <pre className="bg-muted p-2 rounded-md text-xs mt-2">
{`// Correct custom commands format
window.ACCESSTOPIA_CONFIG = {
  commands: {
    "open menu": "toggleMenu",
    "show products": "navigateToProducts",
    "increase brightness": function() {
      // Custom function implementation
    }
  }
};`}
                </pre>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="styling">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center">
              <Zap className="h-4 w-4 mr-2 text-amber-500" />
              Styling and Visual Issues
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-sm mb-1">Widget Styling Conflicts</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If the widget styling conflicts with your website:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>The widget uses Shadow DOM to isolate styles when possible</li>
                  <li>Try customizing appearance with <code>primaryColor</code>, <code>borderRadius</code>, etc.</li>
                  <li>If conflicts persist, adjust the <code>zIndex</code> configuration</li>
                  <li>Move the button position to avoid overlapping with site elements</li>
                  <li>For advanced cases, use the <code>scopeSelector</code> to limit the widget's scope</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Dark Mode Issues</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If dark mode isn't working correctly:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Check if your site already implements dark mode with a different approach</li>
                  <li>The widget adds <code>.accesstopia-dark</code> class to the HTML element</li>
                  <li>Ensure your site's dark mode doesn't conflict with this implementation</li>
                  <li>For custom dark mode integration, use the state change event listener</li>
                </ol>
                <pre className="bg-muted p-2 rounded-md text-xs mt-2">
{`// Custom dark mode integration
document.addEventListener('accesstopia:stateChanged', function(e) {
  if (e.detail.darkMode !== undefined) {
    // Sync with your site's dark mode system
    yourSiteDarkMode.setEnabled(e.detail.darkMode);
  }
});`}
                </pre>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Font Sizing Issues</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If text scaling isn't working properly:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>The widget modifies font sizes using relative units (rem/em)</li>
                  <li>Check if your site uses fixed pixel sizes that override these changes</li>
                  <li>Avoid using <code>!important</code> on font-size properties</li>
                  <li>For complex sites, use the more aggressive <code>forceFontSize</code> option</li>
                </ol>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="performance">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center">
              <Zap className="h-4 w-4 mr-2 text-amber-500" />
              Performance and Technical Issues
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-sm mb-1">Widget Slowing Down Website</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If you notice performance issues:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>The widget loads asynchronously and shouldn't block rendering</li>
                  <li>Advanced features like voice recognition are only activated when used</li>
                  <li>For high-traffic sites, consider lazy loading the widget on user interaction</li>
                  <li>Check browser dev tools performance timeline to identify issues</li>
                  <li>Disable unused features to improve performance</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Settings Not Saving</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If user preferences aren't being saved:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Check if localStorage is available and not full</li>
                  <li>Verify that cookies are not blocked by browser settings</li>
                  <li>Test in different browsers to isolate the issue</li>
                  <li>For privacy-focused browsers, some storage limitations may apply</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Console Errors</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Common error messages and solutions:
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1 text-muted-foreground">
                  <li><code>Failed to load resource</code>: Check network connectivity and CORS settings</li>
                  <li><code>X is not defined</code>: Script loading order issue, ensure dependencies are loaded</li>
                  <li><code>Permission denied</code>: Browser permissions issue, check microphone access</li>
                  <li><code>localStorage is not available</code>: Private browsing mode or storage restrictions</li>
                  <li><code>Cannot read property of undefined</code>: Initialization timing issue</li>
                </ul>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="security">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center">
              <ShieldAlert className="h-4 w-4 mr-2 text-amber-500" />
              Security and Compatibility
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-sm mb-1">Content Security Policy (CSP) Issues</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If CSP is blocking the widget:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Add the following domains to your CSP:</li>
                </ol>
                <pre className="bg-muted p-2 rounded-md text-xs mt-2">
{`Content-Security-Policy:
  script-src 'self' https://*.supabase.co;
  connect-src 'self' https://*.supabase.co;
  img-src 'self' data: https://*.supabase.co;
  style-src 'self' 'unsafe-inline';`}
                </pre>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Cross-Origin (CORS) Issues</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If you're experiencing CORS errors:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>The widget API includes proper CORS headers for all requests</li>
                  <li>Check if your application has CORS restrictions that affect the widget</li>
                  <li>For local development, ensure your server allows cross-origin requests</li>
                  <li>Verify that any proxies or CDNs aren't stripping CORS headers</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">Browser Compatibility</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Browser-specific issues:
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1 text-muted-foreground">
                  <li><strong>Internet Explorer</strong>: Not supported, widget will not function</li>
                  <li><strong>Firefox</strong>: Voice features unavailable, all other features work</li>
                  <li><strong>Safari</strong>: Requires user interaction before enabling voice features</li>
                  <li><strong>Mobile browsers</strong>: Voice support varies, touch-friendly UI provided</li>
                  <li><strong>Chrome/Edge</strong>: Full support for all features</li>
                </ul>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="connectivity">
          <AccordionTrigger className="text-base font-medium">
            <div className="flex items-center">
              <Wifi className="h-4 w-4 mr-2 text-amber-500" />
              Network and Connectivity Issues
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-sm mb-1">Widget Failed to Load</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If the widget script fails to load:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Check your internet connection</li>
                  <li>Verify the script URL is correct and accessible</li>
                  <li>Check if your firewall or security software is blocking the request</li>
                  <li>Inspect network requests in browser dev tools for specific errors</li>
                  <li>Try accessing the widget URL directly to check for server issues</li>
                </ol>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">HTTPS Requirements</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Security-related requirements:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Voice features require HTTPS for microphone access</li>
                  <li>For localhost development, browsers make special exceptions</li>
                  <li>If using HTTP, the widget will automatically disable voice features</li>
                  <li>Always test your production integration over HTTPS</li>
                </ol>
                
                <Alert className="mt-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Starting with Chrome 94+, Safari 15+, and Firefox 97+, microphone access requires a secure context (HTTPS or localhost).
                  </AlertDescription>
                </Alert>
              </div>
              
              <div>
                <h3 className="font-medium text-sm mb-1">API Rate Limits</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  If you encounter rate limiting issues:
                </p>
                <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
                  <li>Free tier: 1,000 widget loads per month</li>
                  <li>Standard tier: 25,000 widget loads per month</li>
                  <li>Premium tier: 100,000+ widget loads per month</li>
                  <li>Voice recognition: 500 commands per day across all users</li>
                  <li>If exceeded, the widget will still load but voice features may be limited</li>
                </ol>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      
      <div className="bg-muted/50 p-4 rounded-lg">
        <h3 className="font-medium mb-2">Still Need Help?</h3>
        <p className="text-sm text-muted-foreground">
          If you're still experiencing issues, please contact our support team with the following information:
        </p>
        <ul className="list-disc pl-5 text-sm mt-2 space-y-1 text-muted-foreground">
          <li>Your installation key (masked for security)</li>
          <li>Website URL where you're implementing the widget</li>
          <li>Browser and version information</li>
          <li>Specific error messages from the console</li>
          <li>Steps to reproduce the issue</li>
        </ul>
        <p className="text-sm mt-3">
          <a href="mailto:support@accesstopia.com" className="text-primary hover:underline">
            Email our support team
          </a> or visit our <a href="https://accesstopia.com/support" className="text-primary hover:underline">
            support portal
          </a> for assistance.
        </p>
      </div>
    </div>
  );
};

export default TroubleshootingGuide;
